/*
This module is responsible for rendering each study row in the study list. This is heavily dependent on the AnalyzeStudy
to generate the data model for reactive groups and outcomes.
*/
import { uniq, escape } from 'lodash'
import { all, Div, reactive, Section, Span, Variable, VBoolean, VArray, not, H3, P, UL, LI } from 'alkali'
import * as Alkali from 'alkali'
import './Study.scss'
import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import { selectedOutcomes, characteristicsAvailable, interventionsAvailable, outcomesAvailable, addCharacteristic } from 'model/EvidenceSearch'
import { studyDetailsPane, detailsExpanded } from 'components/studies/StudyDetailsPane'
import { defaultStudyExpanded, focusedStudy, focusedStudyId, activeTab } from 'model/StudyCollection'
import { analysisType, analyzeTreatmentAs, currentAnalysisSelections, faqOrReadOnly, selectedStudies } from 'model/SearchQuery'
import { computeComparativeEffect, numToString, getOutcomeValue, identifyGroups, identifyGroupArms, StudyAnalysisModel, setUpdateVotes, getCIWidth } from 'model/AnalyzeStudy'
import { inferExtractState } from 'components/extract/model/extraction'
import Icon from 'components/Icon'
import Bubble from 'components/Bubble'
import { StudyGroups } from './StudyGroups'
import * as expandIcon from 'icons/view-1.svg'
import * as layoutIcon from 'icons/layout-2.svg'
import * as positiveDirectionalityIcon from 'icons/monitor-heart-rate-up.svg'
import * as negativeDirectionalityIcon from 'icons/monitor-heart-rate-down.svg'
import * as bubble from 'icons/bubble.svg'
import * as characteristicIcon from 'icons/task-list-heart.svg'
import * as notesIcon from 'icons/notes-paper-text.svg'
import app from 'app/app'
import { DirectionalityDropdown } from './DirectionalityDropdown'
import { InterventionDropdown } from '../filter/InterventionDropdown'
import * as continuityIcon from 'icons/keyboard-asterisk.svg'
import { Toast, getToastPosition, removeToast } from 'components/generic/Toast'
import { voteForArticle, getArticleId, screening } from '../../model/Screening'
import { PromptToScreenDialog } from 'components/screening/PromptToScreenDialog'
import { isBinary } from 'analysis/RowTypeUnit'
import { studiesSearch, studySearchCache } from 'model/EvidenceSearch'
import { get } from 'tools/request'

const scoreColor = (score) => {
	if (score > 84) {
		return 'score-green';
	} else if(score >= 66) {
		return 'score-yellow';
	} else if(score < 66) {
		return 'score-red'
	} else {
		return ''
	}
}

let infinityValue = false
const TestingToast = (studyTitle, toastPosition, studyData) => new Toast(`.${toastPosition.style}.summary-toast.active.hover-toast`, {
	id: studyTitle,
	style: {position: 'absolute', left: toastPosition.x, top: toastPosition.y},
	display : Boolean(studyData.summaryUri),
	hover: true,
	title: 'Study Stats',
	message: new Div([
		UL([
			P('.strong', [
			'General'
			]),
			LI([
				Span('.strong',['Year:']), Span(studyData.study.year),
			]),
			LI([
				Span('.strong' + (studyData.comparativeEffect.effect == '∞' ? '.infinity' : ''), ['Effect:']), Span(studyData.comparativeEffect.effect),
			]),
			LI([
				Span('.strong',['Continuity Applied:']), Span(analysisType != 'bayesian' && studyData.comparativeEffect.continuityApplied),
			]),
			LI([
				Span('.strong',['Comparative:']), Span(studyData.comparativeEffect.isComparative),
			]),
		]),
		UL([
			P('.strong', [
			'Ranking Info'
			]),
			LI([
				Span('.strong',['Score:']), Span(`.${scoreColor((studyData.study.score * 100))}`, `${(studyData.study.score * 100).toFixed(1)}%`),
			]),
			LI([
				Span('.strong',['Matching:']), Span(`.${scoreColor((studyData.study.matchingScore * 100))}`, `${(studyData.study.matchingScore * 100).toFixed(1)}%`),
			]),
		])
	])
})

const ComputedValuesToast = (studyTitle, toastPosition, comparativeEffect, bayesianAnalysis) => console.log({comparativeEffect}) || new Toast(`.${toastPosition.style}.continuity-toast.active.hover-toast`, {
	id: studyTitle,
	style: (toastPosition.style == 'bottom-arrow') ? {position: 'absolute', left: toastPosition.x, bottom: toastPosition.y, top: 'unset'} : {position: 'absolute', left: toastPosition.x, top: toastPosition.y},
	hover: true,
	display: comparativeEffect.property('hasTransforms').to(Boolean),
	title: 'Computed Values',
	message: new Div([
		UL(comparativeEffect.get('transforms')?.map(({ arm, transform }) => LI([ arm + ': ' + transform ]))),
		Div({
			display: comparativeEffect.property('continuityApplied').to(Boolean),
		}, [
			H3('Continuity Correction'),
			P([
				bayesianAnalysis ? 'Basing analysis on zero event counts may not converge to realistic estimates in Bayesian analysis' : 'DocAnalytics automatically checks for problematic zero counts, and adds a fixed value (' + (currentAnalysisSelections.settings.get('continuityCorrection') ?? 0.5) + ') to all cells of study results tables where the problems occur.',
			]),
		]),
	])
})

const displayToast = (studyID, event, comparativeEffect) => {
	const bayesianAnalysis = analysisType == 'bayesian'
	const testToast = studyID.includes('summary')
	const strippedId = studyID.replace(/\s/g, "");

	let getParentData = getToastPosition(event.target),
	{vwPercent, vhPercent, width, closeToEnd, responsiveHeight} = getParentData.position,
	toastPosition

	if (!testToast){
		// If not the test toast then it's the continuity toast
		switch(closeToEnd){
			case 'top':
				toastPosition = { x: `calc(${vwPercent} - 294px)`, y: `calc(${vhPercent} + 115px)`, style: 'top-arrow' }
			break
			case 'bottom':
				toastPosition = { x: `calc(${vwPercent} - 275px)`, y: `calc(${responsiveHeight} + 30px)`, style: 'bottom-arrow'}
			break;
			default:
				toastPosition = { x: `calc(${vwPercent} - 345px)`, y: `calc(${vhPercent} + 70px)`, style: 'right'}
				
		}
		const computedValuesToast = ComputedValuesToast(strippedId, toastPosition, comparativeEffect, bayesianAnalysis)
		const resultBody = document.querySelector('#main')
		const sourcesView = document.querySelectorAll('.sources-view').length > 0
		if (sourcesView) {
			resultBody.prepend(computedValuesToast)
		}
	} else {
		switch(closeToEnd){
			case 'top':
				toastPosition = { x: `calc(${vwPercent} - 325px)`, y: `calc(${vhPercent} + 35px)`, style: 'top-arrow' }
			break;
			case 'bottom':
				toastPosition = { x: `calc(${vwPercent} - 316px)`, y: `calc(${vhPercent} - 225px)`, style: 'bottom-arrow'}
			break;
			default:
				toastPosition = { x: `calc(${vwPercent} - 372px)`, y:  `calc(${vhPercent} - 15px)`, style: 'right'}
		}
		const testToast = TestingToast(strippedId, toastPosition, comparativeEffect)
		const studiesBody = document.getElementById('main')
		studiesBody.prepend(testToast)
	}
}
import { view as analysisView } from 'model/Analysis'
import { isAnalyzable } from '../../../portal/analysis/Effect'

export const PUB_SELECTION_TEMPLATES = {
	resultItem: ({ disabled, id, title, text, source, srcabbrv }) =>
		'<div class="selectivity-result-item' +
		(disabled ? ' disabled' : '') +
		'"' +
		(title ? ` title="${title}"` : '') +
		' data-item-id="' +
		escape(id) +
		'">' +
		escape(text) +
		(srcabbrv ? `<span class='pub-src' title='${source}'>${srcabbrv}</span>` : '') +
		'</div>',
}

const selectedGroup = reactive('')
const interventionCollections = reactive([])
const interventionDropdown = new InterventionDropdown({ interventionCollections, selected: selectedGroup, allowUnselect: true })
let currentDirectionality = reactive([{}])
const directionalityDropdown = new DirectionalityDropdown({
	outcomes: currentDirectionality,
})

export default class Study extends Section('.study') {
	outcomeName: Variable
	currentPublication: Variable
	timepoint: VNumber
	outcome: Variable
	timepoints: Variable
	analyzable: VBoolean
	expanded: VBoolean
	study: {}

	created({ study }) {
		let { name, id, source } = study
		this.rootStudyId = id
		let studyAnalysisModel = this.studyAnalysisModel = new StudyAnalysisModel(study)
		let { publications, currentPublication, interventions, defaultOutcome, outcome, outcomeName, groupAnalyzeAs, timepoint,
			groupsVar, selectedGroups, pubAbbrv, pubSrc, selectedOutcome, isPositiveDirectionality, analyzable,
			nameTooltip, studySettings, isFullyExpanded, currentPublicationId } = studyAnalysisModel
		Object.assign(this, studyAnalysisModel)
		study.analysisModel = studyAnalysisModel
		let groups = currentPublication.property('groups')
		let N = currentPublication.property('N')
		let author = currentPublication.property('author')
		let conditions = currentPublication.property('conditions')

		let isExpanded = (this.expanded = reactive(false))
		if (study.groups?.length > 0 && defaultStudyExpanded.valueOf())
			setTimeout(() => this.expandToggle(true), 10)
		let comparativeEffect = all(
			[outcome, groupsVar, currentAnalysisSelections.settings],
			(outcome, groups, settings) => {
				let { effect, continuityApplied, isComparative, transforms, description } = computeComparativeEffect(
					outcome,
					groups
				)
				if (effect === undefined) return
				let isRatio =
					isBinary(outcome) &&
					(settings.measureBin == 'OR' || settings.measureBin == 'RR' || !settings.measureBin)
				return {
					effect: numToString(effect),
					isPositive: (settings.useSMD && analysisType != 'bayesian') ? effect > 0 :
						(effect > (isRatio ? 1 : 0) == isPositiveDirectionality),
					hasTransforms: continuityApplied || transforms,
					continuityApplied,
					isComparative,
					transforms,
					description: description || ''
				}
			}
		)

		let lastGroupArticleId

		//if multiple designs, remove "Clinical Study" if it exists (since that one is auto-added and not the real one)
		let designs = currentPublication.designs;
		if (designs.valueOf().length > 1)
			designs = designs.filter(d => d != 'Clinical Study')

		let design = designs.to((publication) => publication[0])

		let studySetId, referenceId, summaryUri
		if (app.user?.FeatureConfig?.['All Study Sets']?.Enabled) {
			summaryUri = currentPublication.to((publication) => {
				if (publication.outcomes || publication.hasDocData) {
					let metadata = publication.metadata
					if (metadata.legacyId && metadata.studySetId) {
						referenceId = metadata.legacyId
						studySetId = metadata.studySetId
					} else {
						referenceId = (publication.hasDocData && publication.sourceReferenceId > 0) ?
							publication.sourceReferenceId : publication.id
						studySetId = publication.id + 1000000000
					}
					return (
						(location.hostname == 'localhost'
							? 'https://docdata.doctorevidence.com/'
							: 'docdata.html') + `#!/DocData/StudySet/${studySetId}/Studies/${referenceId}`
					)
				} else {
					return `https://search.doctorevidence.com/details/${publication.id}`
				}
			})
		}
		let groupCount = all([groups, outcome]).to(([groups, outcome]) => {
			let initial = Math.max(groups.filter((group) =>
				outcome.cells.find(cell =>
					cell.group == group.Id && isAnalyzable(cell, outcome))).length, 2)
			return {
				initial,
				total: groups.length,
				fullyExpandable: groups.length > initial,
				expandable: groups.length > 0,
			}
		})
		let vote = currentPublication.to(publication => voteForArticle(publication))
		// let publicationsObject = {
		// 	publications,
		// 	nameTooltip,
		// 	currentPublicationId,
		// 	pubAbbrv,
		// 	pubSrc
		// }
		const extractState = inferExtractState(study, groups, outcome, app.user)

		let returns = {
			classes: {
				analyzable: analyzable.property('isAnalyzable'),
				'qualitative-analyzable': analyzable.property('isQualitative'),
				'potentially-analyzable': analyzable.property('isPotentiallyAnalyzable'),
				'study--expanded': isFullyExpanded,
				expanded: isExpanded,
				'study-fully-expandable': groupCount.property('fullyExpandable'),
				expandable: groupCount.property('expandable'),
				'positive-directionality': isPositiveDirectionality,
				'comparative-effect': comparativeEffect.property('isComparative'),
				'screening-included': vote.to(vote => vote?.type == 'Include'),
				'screening-uncertain': vote.to(vote => vote?.type == 'Uncertain'),
				'screening-excluded': vote.to(vote => vote?.type && (vote.type != 'Include' && vote.type != 'Uncertain')),
				'screening-excluded-design': vote.to(vote => vote?.type && vote.type == 'Wrong study design'),
				'screening-excluded-population': vote.to(vote => vote?.type && vote.type == 'Wrong population'),
				'screening-excluded-intervention': vote.to(vote => vote?.type && vote.type == 'Wrong intervention'),
				'screening-excluded-outcome': vote.to(vote => vote?.type && vote.type == 'Wrong outcome'),
				'screened': vote.to(vote => vote && vote.type != 'Uncertain'),
				'focused-study': focusedStudyId == study.id,
				study: true,
			},
			style: {
				'--selected-height': groupCount.to(({ initial }) => initial * 2.2 + 0.3 + 'rem'),
			},
			onclick(event) {
				if (analysisView == 'forest-plot')
					return
				else if (
						activeTab == 'screening' || activeTab == 'extract' ||
						detailsExpanded.valueOf() && !event.target.classList.contains('study-expander')) {
						focusedStudy.put(currentPublication.valueOf())
					studyDetailsPane.showStudy(this.study, extractState)
				} else if (this.querySelector('header').contains(event.target))
					this.expandToggle()
			},
			content: [
				<header
					class="study-head"
					onclick={(event) => {
					}}>
					<Bubble studyId={study.id} icon={bubble} />
					<div
						class="study-head-item study-toggle"
						title={analyzable.to(({ isAnalyzable, reason }) =>
							isAnalyzable
								? all([studySettings, comparativeEffect], (settings, effect) =>
											((reason || effect?.isComparative) ?
											"This study's data can be included in the Evidence Summary and the final analysis" :
											'This study can be used for analysis of absolute effects. This can will be included in the Evidence Summary, but not the final analysis.') +
											(settings?.weighting
												? ' and has ' + Math.round(settings.weighting) + '% weighting'
												: '')
								  )
								: reason
						)}
						style={{
							'--study-weighting': studySettings.to(
								(settings) => (settings?.weighting + 10) / 110 || 1
							),
						}}
						onclick={(event) => {
							event.stopPropagation()
							if (faqOrReadOnly.valueOf())
								return
							if (analyzable.get('isAnalyzable') ||
									analyzable.get('isQualitative') ||
									this.classList.contains('dgrid-selected')) {
								let selecting = !this.classList.contains('dgrid-selected')
								if (selecting && (screening.valueOf() && vote.valueOf()?.type != 'Include'))
									return new PromptToScreenDialog({ selectedStudy: currentPublication.valueOf() }).show(event)
								$(this).closest('.dgrid')[0].userSelect(this, null, selecting) // toggle selection
							}
						}}
					/>
					<div class="study-head-item study-expander"></div>
					<Icon class="expand-study-details study-head-item selection-menu-icon" title="View study details" icon={ expandIcon } onclick={(event) => {
						event.stopPropagation()
						if (event.ctrlKey) {
							return
						}
						studyDetailsPane.showStudy(currentPublication.valueOf(), extractState)
					}} />
					<div class="study-head-item study-year-name" title={nameTooltip}>
						{this.publications.to((publicationOptions) => {	
							if (publicationOptions.length > 0) {
								return [
									<SelectivityDropdown
										class="study-head-item study-year-author-select"
										showSearchInputInDropdown={false}
										onpointerdown={(event) => event.stopPropagation()}
										onclick={(event) => event.stopPropagation()}
										value={currentPublicationId}
										items={publicationOptions}
										readOnly={faqOrReadOnly.valueOf()}
										templates={PUB_SELECTION_TEMPLATES}>
										<span class="publication-count">{study.additionalStudies.length + 1}</span>
									</SelectivityDropdown>,
								]
							} else {
								return [
									<div class="study-head-dditem study-year">{study.year || 'NR'}</div>,
									<div class="study-head-item study-name">
										<span class="study-name-name" title={nameTooltip}>
											{name || 'Unknown'}
										</span>
										{pubAbbrv ? (
											<span class="pub-src" title={pubSrc}>
												{pubAbbrv}
											</span>
										) : null}
									</div>,
								]
							}
						})}
					</div>
					<div class="study-head-item study-title">
						<div class="study-head-item-conditions">
							{conditions.map((condition) => (
								<span
									class="study-condition"
									title={condition.label || condition}
									onclick={(event) => {
										if (condition.id) {
											addCharacteristic(condition)
										}
									}}
									onpointerdown={(event) => {
										event.stopPropagation()
									}}>
									{condition.label || condition}
								</span>
							))}
						</div>
						<div class="study-head-item-interventions">
							{interventions.map((intervention) => (
								<span class="study-intervention" title={intervention}>
									{intervention}
								</span>
							))}
						</div>
						<div class="study-head-item-title" title={currentPublication.title} innerHTML=
							{currentPublication.to(annotateTitle)}>
						</div>
						<div class="study-report"
							onmouseenter={(event) => {
								displayToast(`continuity-${id}`, event, comparativeEffect)
							}}
							onmouseleave={() => {
								removeToast(`continuity-${id}`)
							}}
							>
							<div
								class="study-report-treatment-circle"
								display={comparativeEffect.to(Boolean)}
								classes={{
									'positive-effect': comparativeEffect.property('isPositive'),
								}}
								>
								<Icon
									class="info-warning shadow continuity-icon"
									display={comparativeEffect.property('hasTransforms').to(Boolean)}
									icon={continuityIcon}>
								</Icon>
							</div>
							<div class={"study-report-treatment-value" + (comparativeEffect.property('effect').valueOf() == '∞' ? " infinity" : "")} display={comparativeEffect.to(Boolean)} title={comparativeEffect.property('description')}>
							{comparativeEffect.property('effect')}
							</div>
							
							<div class="forest-axis"></div>
						</div>
					</div>
					<div class="study-head-item study-design study-meta-pills">
						{design ? (
							<div title={design.to((design) => design || 'Unknown study design')}>
								{design.to((design) => shortDesignName[design] || design || 'NR')}
							</div>
						) : (
							null
						)}
						{!isFAQDashboard ? (<a
							class="summary-link"
							href={summaryUri}
							display={Boolean(summaryUri)}
							target="_blank"
							>
							<Icon icon={layoutIcon} 
								onmouseenter={(event) => {
									const studyData = {
										comparativeEffect,
										summaryUri,
										currentAnalysisSelections,
										study
									}
									displayToast(`summary-${id}`, event, studyData)
								}}
								onmouseleave={() => {
									removeToast(`summary-${id}`)
								}} 
								/>
						</a>) : null
						} 
					</div>
				</header>,
				<div
					class="study-body"
					onpointerdown={(event) => event.stopPropagation()}
					classes={{
						'hide-body': currentPublication.to(
							(publication) => !(publication.groups?.length > 0)
						),
					}}>
					<div className="study-body-meta" />
					<div className="study-body-Ns" title={ N.to((N) => `There were ${N} participants in this study.`) }>
						<div class="study-head-item study-N" dataset={{ N: N.to((N) => shortN(N)) }} />
					</div>
				</div>,
				<div
					class="study-footer"
					onclick={(e) => {
						e.stopPropagation()
						if (!isFullyExpanded.valueOf()) isExpanded.put(true)
						isFullyExpanded.put(!isFullyExpanded.valueOf())
						requestAnimationFrame(() => {
							this.style.setProperty('--fully-expanded-height', this.scrollHeight + 4 + 'px')
						})
					}}
					dataset={{
						moreGroups: groupCount.to(
							({ initial, total }) =>
								'+' + (total - initial) + (total - initial > 1 ? ' Groups' : ' Group')
						),
					}}
					title={ isFullyExpanded.to(expanded => expanded ? 'Click to collapse back to primary groups of interest' : 'Click to view additional groups within this study')}
					onpointerdown={(e) => e.stopPropagation()}
				/>,
			],
		}
		this.renderStudyBody = () => {
			this.querySelector('.study-body-meta').append(this.renderMetadata(currentPublication))
			let studyBodyNs = this.querySelector('.study-body-Ns')
			studyBodyNs.innerHTML = ''
			studyBodyNs.append(
				groupsVar.to((groups) => {
					return [
						<div class="study-head-item study-N" dataset={{ N: N.to((N) => shortN(N)) }} />,
						...groups.map((group, groupIndex) => {
							let { N: groupN, OriginalName: name } = group
							return (
								<div
									class="study-group-N"
									dataset={{
										N: this.getOutcomeCell(group).to((cell) => shortN(cell?.N || groupN)),
									}}
									title={ this.getOutcomeCell(group).to((cell) => {
										let tooltip = groupN > 0 ? [`There were ${groupN} patients in the ${name} group`] : []
										if (cell?.N > 0 && cell?.N != group.N) {
											tooltip.push(`There were ${cell.N} patients observed for this outcome`)
										}
										return tooltip.join('. ') + '.'
									}) }
									style={{ '--group-distance': groupIndex * 2.2 + 'em' }}
								/>
							)
						}),
					]
				})
			)
			this.querySelector('.study-body').append(
				<StudyGroups groups={ groupsVar } outcome={ outcome }
					getOutcomeValue= { group => outcome.to(outcome => this.getOutcomeValue(group)) }
					groupAnalyzeAs={ groupAnalyzeAs }
					treatmentCircle={comparativeEffect.to(Boolean)}
					groupMenu ={(event, group, value) => {
						if (!value && !group.analyzeAs) {
							return alert('There is no measurable value for this outcome, so this group can not be selected for analysis.')
						}
						interventionCollections.put(uniq(Array.from(analyzeTreatmentAs.valueOf().values())))
						selectedGroup.put(group.OriginalName)
						interventionDropdown.show(event)
						interventionDropdown.onSelection = (asGroup) => {
							let newGroupsAnalyzeAs = (groupAnalyzeAs.valueOf() || []).filter(
								({ analyzeLabel, groupId }) => groupId != group.Id
							)
							newGroupsAnalyzeAs.push({
								groupId: group.Id,
								analyzeLabel: asGroup,
							})
							groupAnalyzeAs.put(newGroupsAnalyzeAs)
							selectedStudies.updated() // the effective study selection can change due to this change as well
						}
					}}
				/>
			)
			this.renderedBody = true
		}
		return returns
	}

	expandToggle(value) {
		if (this.expanded.valueOf() === value) return
		let expand = typeof value == 'boolean' ? value : !this.expanded.valueOf()
		this.expanded.put(expand)
		if (!expand) this.isFullyExpanded.put(false)
		if (!this.renderedBody) this.renderStudyBody()
		try {
			if (this.localName && this.classList.contains('expanded')) {
				let svg = this.querySelector('#goo-' + this.rootStudyId)
				svg.children[1].children[0].beginElement()
				this.querySelector('.study-body-Ns').setAttribute(
					'style',
					'filter: url(#goo-' + this.rootStudyId + ');'
				)
				this.querySelector('.study-body-Ns').classList.add('bubbles')
			} else if (this.localName && !this.classList.contains('expanded')) {
				let svg = this.querySelector('#goo-' + this.rootStudyId)
				svg.children[1].children[0].beginElement()
				this.querySelector('.study-body-Ns').setAttribute(
					'style',
					'filter: url(#goo-' + this.rootStudyId + ');'
				)
				this.querySelector('.study-body-Ns').classList.add('bubbles')
				return
			}
		} catch (error) {
			//temporarily for IE
			console.error(error)
		}
	}

	renderMetadata(currentPublication) {
		let outcome = this.outcome
		let gridOutcome = selectedOutcomes.valueOf()?.[0]
		let nonMatchingDirectionality = this.outcome.to((outcome) => {
			return (
				Boolean(gridOutcome?.isPositive) !=
				Boolean(outcome?.isPositive || outcome?.suggestedPositive)
			)
		})
		let content = []
		let outcomeNames = currentPublication.to((publication) => {
			let outcomeNames = new Set()
			for (let outcomeRow of publication.outcomes || []) {
				outcomeNames.add(outcomeRow.name)
			}
			return Array.from(outcomeNames).map((name) => ({
				id: name,
				text: name,
			}))
		})
		let selectedTimepoint = all(
			[this.timepoints, this.timepoint],
			(timepoints, timepoint) =>
				timepoints.find((o) => o.time?.days == timepoint) || timepoints[0]
		)
		let outcomeTitle = outcome.to((outcome) => {
			let note = ''
			if (outcome) {
				note += outcome.name
				if (outcome.definition) note += '\n' + outcome.definition
				if (outcome.method) note += '\nMethod of analysis: ' + outcome.method
			}
			return note
		})
		content = [
			<div class="study-outcome-name" display={outcome.to(Boolean)} title={outcomeTitle}>
				{outcomeNames.to((outcomeNames) =>
					outcomeNames.length > 1 ? (
						<SelectivityDropdown
							items={outcomeNames}
							showSearchInputInDropdown={false}
							value={this.outcomeName}
							onclick={(event) => event.stopPropagation()}
							readOnly={faqOrReadOnly.valueOf()}
						/>
					) : (
						<span class="metadata-text">{outcome.property('name')}</span>
					)
				)}
			</div>,
			<div class="study-meta-additional" display={outcome.to(Boolean)}>
				<div
					class="study-directionality"
					classes={{ 'non-matching-directionality': nonMatchingDirectionality }}>
					<Icon
						class="summary-link"
						icon={this.outcome.to((outcome) =>
							(
								typeof outcome?.isPositive == 'boolean'
									? outcome.isPositive
									: outcome?.suggestedPositive
							)
								? positiveDirectionalityIcon
								: negativeDirectionalityIcon
						)}
						title={this.outcome.to(
							(outcome) =>
								outcome &&
								((
									typeof outcome?.isPositive == 'boolean'
										? outcome.isPositive
										: outcome.suggestedPositive
								)
									? 'Increasing ' + outcome.name + ' is the desired outcome'
									: 'Reducing ' + outcome.name + ' is the desired outcome')
						)}
						onclick={(event) => {
							if (faqOrReadOnly.valueOf())
								return
							let outcome = this.outcome.valueOf()
							let id = outcome.name
							if (outcome.type?.startsWith('Time')) {
								id = outcome.type + ' ' + id
							}
							currentDirectionality.put([
								{
									id,
									label: outcome.name,
									isPositive:
										typeof outcome?.isPositive == 'boolean'
											? outcome.isPositive
											: outcome.suggestedPositive,
								},
							])
							if (typeof outcome.isPositive == 'boolean')
								alert('This outcome has a fixed directionality. Please contact support@drevidence.com if you believe it is wrong.')
							else if (!app.user?.Permissions.TermMaster.Entity?.Edit)
								alert('You do not have permission to update directionality. Please contact support@drevidence.com for assistance.')
							else directionalityDropdown.show(event)
							directionalityDropdown.onChange = (isPositive) => {
								this.outcome.valueOf().suggestedPositive = isPositive
								this.outcome.updated()
							}
						}}
					/>
				</div>
				<div class="study-notes" classes={{ hidden: not(currentPublication.to(study => study.notes?.length)) }}>
					<Icon icon={notesIcon} title={ currentPublication.to(study => study.notes?.join('\n') || '') }/>
				</div>
				<div class="characteristic-indicators">
					{currentPublication.characteristics.map((characteristic) => (
						<Icon
							icon={characteristicIcon}
							title={characteristic.name + ': ' + characteristic.number + '%'}
						/>
					))}
				</div>
				<div
					class="study-timepoint"
					title={selectedTimepoint.to((timepoint) => timepoint?.text?.length > 10 ? timepoint?.text : '')}>
					{this.timepoints.to((timepoints) =>
						timepoints.length > 1 ? (
							<SelectivityDropdown
								items={timepoints}
								showSearchInputInDropdown={false}
								value={this.timepoint}
								onclick={(event) => event.stopPropagation()}
								readOnly={faqOrReadOnly.valueOf()}
							/>
						) : (
							<span class="metadata-text">
								{selectedTimepoint.to((timepoint) => timepoint?.text)}
							</span>
						)
					)}
				</div>
			</div>,
		]
		return content
	}
	onArticleAdded() {
		this.publications.updated()
	}
	getOutcomeValue(group, replicateR?, continuityCorrection?) {
		return this.outcome.to((outcome) => {
			return getOutcomeValue(outcome, group, replicateR, continuityCorrection)
		})
	}
	getOutcomeCell(group) {
		return this.outcome.to((outcome) => {
			let cells = outcome?.cells
			return cells && group && cells.find((cell) => cell.group == group.Id)
		})
	}
	getRowData() {
		return this.studyAnalysisModel.getRowData()
	}

	reportAnalyzedEffect(effect, variance, scale) {
		let N = 0, weight = 0
		let ciWidth = getCIWidth()
		this.classList.remove('no-results') // just in case it was previously unanalyzable

		for (let group of this.study.groups) {
			if (group.analyzeAs) {
				let cell = this.getOutcomeCell(group).valueOf()
				let cellN = cell?.N || group.N
				if (cellN) {
					N += cellN
					weight = cellN * (weight ? weight / (cellN + weight) : 1)
				}
			}
		}
		this.querySelector('.study-N').dataset.N = N
		let size = (Math.pow(weight, 0.3) / 2 + 2) / 5
		let position = effect * scale
		let confidenceIntervalWidth = Math.abs(Math.sqrt(variance) * ciWidth * scale)
		let comparativeEffectCircle = this.querySelector('.study-report-treatment-circle')
		comparativeEffectCircle.style.left =
			'calc(' + (position + 40) + '%' + ' - ' + size / 2 + 'rem)'
		comparativeEffectCircle.style.height = comparativeEffectCircle.style.width = size + 'rem'
		comparativeEffectCircle.style.top = 1.5 - size / 2 + 'rem'

		let intervalBar = this.querySelector('.study-report').append(
			outcomeIntervalBar(confidenceIntervalWidth)
		)
		intervalBar.style.left = position + 40 - confidenceIntervalWidth + '%'
	}
	switchToSourceView() {
		this.querySelector('.study-N').dataset.N = this.currentPublication.N.to((n) => shortN(n))
		let comparativeEffectCircle = this.querySelector('.study-report-treatment-circle')
		comparativeEffectCircle.style.left = comparativeEffectCircle.style.top = ''
		comparativeEffectCircle.style.height = comparativeEffectCircle.style.width = ''
		let effectBar = this.querySelector('.confidence-bar')
		effectBar?.parentNode?.removeChild(effectBar)
	}
}

setUpdateVotes(updateVotes)
async function updateVotes(excludeStudy, newStudy) {
	const studyVote = voteForArticle(excludeStudy.valueOf()).valueOf()
	if (!studyVote || studyVote.type !== "Include") return
	else {
		if (window.confirm(`You have previously included ${excludeStudy.name} in your study selections. Do you want to reject your previous selection and now include ${newStudy.name}?`)) {
			await screening.flag({
				type: 'Overlapping population',
				reason: 'Overlapping population',
				notes: excludeStudy.notes || '',
				articleIds: [getArticleId(excludeStudy)], // previous selection
				screeningId: studyVote.screeningId,
			}).catch(error => console.log(error))
			await screening.flag({
				type: 'Include',
				articleIds: [getArticleId(newStudy)], // new selection
				notes: excludeStudy.notes || '',
				screeningId: studyVote.screeningId, 
			}, newStudy).catch(error => console.log(error))
		}
	}
	return 
}

export const shortN = (N) => (N == null ? 'NR' : N >= 10000 ? Math.round(N / 1000) + 'K' : N)

export const outcomeIntervalBar = (rangeSide) => {
	let bar = new Div('.confidence-bar')
	setTimeout(() => {
		bar.style.width = rangeSide * 2 + '%'
	}, 100)
	return bar
}
export const shortDesignName = {
	'Before and After Trial': 'Observational, Other',
	'Case Control': 'Retrospective',
	'Case Report': 'Retrospective',
	'Case Series': 'Retrospective',
	'Cluster RCT': 'RCT',
	'Cohort Study': 'Observational, Other',
	'Controlled Before and After Trial': 'Observational, Other',
	'Cross-Sectional': 'Observational, Other',
	'Cross Sectional Study': 'Observational, Other',
	'Expanded Access Program': 'Prospective',
	'Follow-up/Extension': 'Interventional, Other',
	'Interim Analysis': 'Interventional, Other',
	'Literature Review': 'Meta/SR',
	'Non-Comparative, Other': 'Interventional, Other',
	'Non-Controlled Clinical Trial': 'Interventional, Other',
	'Non-Randomized Controlled Trial': 'Interventional, Other',
	'Non-Randomized Crossover': 'Interventional, Other',
	'Non-Randomized Non-Controlled Trial': 'Interventional, Other',
	'Observational Non-Comparative Study': 'Observational, Other',
	'Observational, Other': 'Observational, Other',
	'Pooled Analysis': 'Post hoc Analysis',
	'Posthoc Analysis': 'Post hoc Analysis',
	'Prospective Cohort Study': 'Prospective',
	'Prospective Observational': 'Prospective',
	'Qualitative Research': 'Observational, Other',
	'Randomized Controlled Trial': 'RCT',
	'Randomized Crossover': 'RCT',
	'Randomized Non-Controlled Trial': 'Interventional, Other',
	'Retrospective Cohort Study': 'Retrospective',
	'Retrospective Observational': 'Retrospective',
	'Sub-Group Analysis': 'Post hoc Analysis',
	'Clinical Study': 'Interventional, Other',
	'Controlled Clinical Trial': 'Interventional, Other',
	'Clinical Trial  Phase I': 'RCT',
	'Comparative Study': 'Interventional, Other',
}

const longPicoType = {
	int: 'intervention',
	char: 'condition',
	out: 'outcome',
}
function annotateTitle(study) {
	let title = study.title
	let annotatedTitle = ''
	let lastEnd = 0
	for (let { span, type, matched } of study.highlights || []) {
		let [ start, length ] = span || []
		let end = start + length
		let tooltip = matched ? 'Matched ' + matched.label : ''
		let matchedId = matched?.id
		let definition, attribute
		if (type == 'char')
			attribute = findAttribute(characteristicsAvailable, matchedId)
		else if (type == 'int')
			attribute = findAttribute(interventionsAvailable, matchedId)
		else if (type == 'out')
			attribute = findAttribute(outcomesAvailable, matchedId)
		if (attribute?.definition) {
			if (tooltip)
				tooltip += '\n\n'
			tooltip += attribute.label + ':\n' + attribute.definition
		}
			
		annotatedTitle += encodeHTML(title.slice(lastEnd, start)) + '<mark class="annotated-' +
			longPicoType[type] + '"' +
			(tooltip ? ' title="' + encodeHTML(tooltip) + '">' : '>') + encodeHTML(title.slice(start, end)) + '</mark>'
		lastEnd = end
	}
	annotatedTitle += encodeHTML(title.slice(lastEnd, title.length))
	return annotatedTitle
	function findAttribute(available, id) {
		for (let attribute of available.valueOf() || []) {
			if (attribute.id == id)
				return attribute
			if (attribute.children) {
				attribute = findAttribute(attribute.children, id)
				if (attribute)
					return attribute
			}
		}
	}
}

function encodeHTML(string) {
	return string.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}
